import React, { useState, useEffect } from 'react';
import { AlignJustify, X } from 'lucide-react';
import { PageGrid } from './PageGrid';
import { NavDropdown } from './NavDropdown';
import { SiteBrandLink } from './SiteBrandLink';
import { DOWNLOAD_MAC_URL } from '../constants/download';
import {
  SITE_NAV_MOBILE_LINKS,
  SITE_NAV_PAGES,
  SITE_NAV_PRODUCT,
  SITE_NAV_RESOURCES,
} from '../constants/siteNav';

const APP_URL = import.meta.env.VITE_APP_URL || 'http://localhost:5173';

function useScrolled(threshold = 1) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > threshold);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [threshold]);

  return scrolled;
}

export function TopNav() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const scrolled = useScrolled();

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  return (
    <>
      <PageGrid
        as="header"
        className={`sticky top-0 z-50 w-full pt-6 pb-4 transition-[background-color,backdrop-filter] duration-300 ${scrolled ? 'bg-surface-base/80 backdrop-blur-md' : ''}`}
      >
        <div className="col-span-full relative flex items-center justify-between">
          <SiteBrandLink />

          <nav className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-6 md:flex">
            <NavDropdown trigger="Product" items={SITE_NAV_PRODUCT} />
            {SITE_NAV_PAGES.map(({ label, href }) => (
              <span key={label} className="site-nav-link-slot">
                <a href={href} className="site-nav-link">
                  {label}
                </a>
              </span>
            ))}
            <NavDropdown trigger="Resources" items={SITE_NAV_RESOURCES} />
          </nav>

          <a href={`${APP_URL}/login`} className="site-btn-primary hidden md:flex">
            Open App
          </a>

          <div className="flex items-center gap-3 md:hidden">
            <a href={DOWNLOAD_MAC_URL} className="site-btn-primary">
              Download
            </a>
            <button
              type="button"
              onClick={() => setMobileOpen((prev) => !prev)}
              className="text-text-primary transition-colors"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X size={24} /> : <AlignJustify size={24} />}
            </button>
          </div>
        </div>
      </PageGrid>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 bg-surface-base md:hidden">
          <div className="px-[var(--grid-margin)] pt-6">
            <div className="flex items-center justify-between">
              <SiteBrandLink onClick={() => setMobileOpen(false)} />
              <div className="flex items-center gap-3">
                <a href={DOWNLOAD_MAC_URL} className="site-btn-primary">
                  Download
                </a>
                <button
                  type="button"
                  onClick={() => setMobileOpen(false)}
                  className="text-text-primary transition-colors"
                  aria-label="Close menu"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <nav className="flex flex-col gap-4 pt-20">
              {SITE_NAV_MOBILE_LINKS.map(({ label, href }) => (
                <a
                  key={label}
                  href={href}
                  onClick={() => setMobileOpen(false)}
                  className="text-h2 text-text-primary transition-colors hover:text-text-secondary"
                >
                  {label}
                </a>
              ))}
            </nav>
          </div>
        </div>
      )}
    </>
  );
}
